/* Boost recommendations in the cart drawer.

   The widget id comes from theme settings. Everything else - algorithm, model,
   limit, layout, merchandising rules - is read from window.boostWidgetIntegration
   at runtime, so changing the widget in the Boost admin changes the storefront
   without a theme deploy.

   Nothing here depends on the Boost storefront bundle having initialised. It
   only reads the integration config Boost prints into the page, and talks to the
   recommend and analytics endpoints directly. */
(() => {
  const CFG = window.ClothkitsBoostCartRecs;
  if (!CFG || !CFG.enabled || !CFG.wid) return;

  /* routes.root_url is "/" on the primary locale and "/en-gb" elsewhere. Joined
     naively the first gives "//cart.js", which a browser reads as a
     protocol-relative URL and sends to a host called cart.js rather than to the
     shop. Normalise to "" or "/en-gb" once, here. */
  const ROOT = (CFG.rootUrl || '').replace(/\/+$/, '');

  const RECOMMEND_URL = 'https://services.mybcapps.com/discovery/recommend/';
  const ANALYTICS_URL = 'https://lambda.mybcapps.com/e';
  const ANON_CUSTOMER = '00000000-0000-0000-5000-000000000000';

  /* ---------------------------------------------------------------- config */

  /* recommendationWidgets sits at boostWidgetIntegration.TAEApp.modules[6].block
     .app today, but that module index is just this store's block ordering and
     moves whenever the app embed changes. Walking for the key is a few
     milliseconds once per page and survives that. */
  function findWidgetConfig(wid) {
    const roots = Object.keys(window)
      .filter((k) => /^boost/i.test(k))
      .map((k) => window[k]);
    const seen = new WeakSet();
    const stack = roots.filter((r) => r && typeof r === 'object');
    let depth = 0;

    while (stack.length && depth < 20000) {
      depth += 1;
      const node = stack.pop();
      if (!node || typeof node !== 'object' || seen.has(node)) continue;
      seen.add(node);

      const groups = node.recommendationWidgets;
      if (groups && typeof groups === 'object') {
        for (const placement of Object.keys(groups)) {
          const group = groups[placement];
          if (group && typeof group === 'object' && group[wid]) {
            return { widget: group[wid], placement, translations: node.translation };
          }
        }
      }

      for (const key of Object.keys(node)) {
        const value = node[key];
        if (value && typeof value === 'object') stack.push(value);
      }
    }
    return null;
  }

  /* A rule with no conditions and no consequences is what the Boost admin
     leaves behind for a widget that has never had merchandising set up. Sending
     it is not harmless: the API treats it as a rule that matches nothing and
     returns zero products, with a 200 and no error. Verified against this store
     - identical requests return 3,607 candidates without it and 0 with it. Real
     rules are passed through untouched. */
  function usableRules(rules) {
    if (!Array.isArray(rules)) return [];
    return rules.filter((rule) => {
      if (!rule || typeof rule !== 'object') return false;
      const conditions = Array.isArray(rule.conditions) ? rule.conditions.length : 0;
      const consequences = Array.isArray(rule.consequences) ? rule.consequences.length : 0;
      return conditions > 0 || consequences > 0;
    });
  }

  /* ----------------------------------------------------------------- money */

  function formatMoney(amount) {
    const value = Number(amount);
    if (!isFinite(value)) return '';
    const format = CFG.moneyFormat || '£{{amount}}';
    const withCommas = (n, decimals) => {
      const fixed = n.toFixed(decimals);
      const [whole, part] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return part ? `${grouped}.${part}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      switch (token) {
        case 'amount_no_decimals':
          return withCommas(value, 0);
        case 'amount_with_comma_separator':
          return withCommas(value, 2).replace(/,/g, ' ').replace('.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withCommas(value, 0).replace(/,/g, ' ');
        default:
          return withCommas(value, 2);
      }
    });
  }

  /* --------------------------------------------------------------- request */

  async function fetchRecommendations(widget, productIds) {
    const params = widget.params || {};
    const query = new URLSearchParams({
      recommendationType: params.recommendationType || 'related-items',
      limit: String(params.limit || 12),
      shop: CFG.shop,
      wid: CFG.wid,
      event_type: 'init',
      pg: 'cart',
      layoutDisplay: params.layoutDisplay || 'carousel',
      return_all_currency_fields: 'true',
      locale: CFG.locale || 'en',
      currency_rate: '1.0',
      currency: CFG.currency,
      country: CFG.country || '',
      customer_id: CFG.customerId || ANON_CUSTOMER
    });

    const body = {
      shop: CFG.shop,
      recommendationType: params.recommendationType || 'related-items',
      limit: params.limit || 12,
      layoutDisplay: params.layoutDisplay || 'carousel',
      productIds,
      customerId: CFG.customerId || ANON_CUSTOMER
    };
    if (params.modelType) body.modelType = params.modelType;
    if (params.secondaryAlgorithm) body.secondaryAlgorithm = params.secondaryAlgorithm;

    const rules = usableRules(params.rules || params.ruleBased);
    if (rules.length) body.rules = rules;

    /* GET is not supported by this endpoint, it 404s. */
    const response = await fetch(`${RECOMMEND_URL}?${query}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(`recommend ${response.status}`);
    return response.json();
  }

  /* -------------------------------------------------------------- tracking */

  /* Payload follows Boost's event tracking spec for API builds. Field names are
     not guessable from the bundle: it is tid, not da, and eid/sid/t/a are all
     required. Verified against what Boost's own script posts on this store. */

  function uuid() {
    const helper = window.boostWidgetIntegration && window.boostWidgetIntegration.generateUuid;
    if (typeof helper === 'function') {
      try {
        const value = helper.call(window.boostWidgetIntegration);
        if (value) return String(value);
      } catch (error) {
        /* fall through */
      }
    }
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /* Boost writes its localStorage values JSON-encoded, so they come back wrapped
     in literal quote characters. Sending those through unstripped breaks
     attribution silently, which the spec calls out for sid and is just as true
     of the cart tokens. */
  function readBoostLocal(key) {
    let value = null;
    try {
      value = localStorage.getItem(key);
    } catch (error) {
      return null;
    }
    if (!value) return null;
    return String(value).replace(/^"|"$/g, '');
  }

  /* Boost's own helper when the front end library is on the page, so the session
     matches the one its widgets report against. The manual path writes the same
     boostSdSessionId key for the same reason. Values are stripped of wrapping
     quotes: a JSON-serialised session id in localStorage is a known corruption
     case and breaks session attribution without erroring. */
  function sessionId() {
    const helper = window.boostWidgetIntegration && window.boostWidgetIntegration.getSessionId;
    if (typeof helper === 'function') {
      try {
        const value = helper.call(window.boostWidgetIntegration);
        if (value) return String(value).replace(/^"|"$/g, '');
      } catch (error) {
        /* fall through to the manual path */
      }
    }
    let stored = readBoostLocal('boostSdSessionId');
    if (!stored) {
      stored = uuid();
      try {
        localStorage.setItem('boostSdSessionId', stored);
      } catch (error) {
        /* private mode, session id stays per-event */
      }
    }
    return String(stored).replace(/^"|"$/g, '');
  }

  /* rid ties an event back to the response that produced the impression. Boost
     drops events without one, so there is no point firing before a response has
     landed. */
  function track(userAction, rid, product, variantId, cartToken) {
    if (!rid) return;

    const params = (state.widget && state.widget.params) || {};
    const payload = {
      tid: CFG.shop,
      /* qs maps an event to a search or collection report. A recommendation
         widget has neither context. Boost's own recommendation events send it
         as an explicit null rather than dropping the key, so this matches. */
      qs: null,
      eid: uuid(),
      rid,
      pid: product ? String(product.id) : '',
      t: new Date().toISOString(),
      u: userAction,
      /* Every event from this widget comes from the recommend endpoint, so this
         is the actual source rather than a default standing in for one. */
      a: 'recommend',
      r: document.referrer || '',
      sid: sessionId(),
      cid: CFG.customerId ? String(CFG.customerId) : ANON_CUSTOMER,
      pg: CFG.pageType || '',
      vid: variantId ? String(variantId) : '',
      bpids: '',
      wid: CFG.wid,
      rt: params.recommendationType || '',
      actionBundle: false,
      bundle_info: {}
    };

    /* ct is the /cart.js token passed through untouched, including the ?key=
       fragment Shopify puts in it. The written spec says to strip everything
       from ? onward, but the demo store, which the same spec says to mirror,
       sends the whole thing, and so does this store's own Boost script:
         ct = "hWNGu0f8jyDTs2QLMsdh396p?key=49f8381aa8adbf9873dd0d4291cfe2c9"
       Stripping it here would send a token that matches nothing Boost recorded.

       bct is Boost's own cart token, which it keeps in localStorage. Read rather
       than generated, so our events carry the same one Boost's do. */
    if (cartToken) {
      payload.ct = String(cartToken);
      /* Both cart tokens travel together. The native view_product event carries
         neither, so bct is gated on the same condition rather than sent always. */
      const boostCartToken = readBoostLocal('boost_cart_token');
      if (boostCartToken) payload.bct = boostCartToken;
    }

    /* text/plain, not application/json. sendBeacon cannot issue a CORS preflight,
       and any content type outside the three CORS-safelisted ones forces one, so
       an application/json body is rejected by the browser before it leaves. That
       failure surfaces as a red entry in the network panel with no response.
       Boost's own script posts text/plain;charset=UTF-8 for the same reason. */
    const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain;charset=UTF-8' });
    if (navigator.sendBeacon && navigator.sendBeacon(ANALYTICS_URL, blob)) return;
    fetch(ANALYTICS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(() => {});
  }

  /* ---------------------------------------------------------------- render */

  function imageFor(product) {
    const info = Array.isArray(product.images_info) ? product.images_info[0] : null;
    if (info && info.src) return { src: info.src, alt: info.alt || product.title };
    const images = product.images;
    if (images && typeof images === 'object') {
      const first = images['1'] || Object.values(images)[0];
      if (first) return { src: first, alt: product.title };
    }
    return null;
  }

  function priceHTML(product) {
    const min = Number(product.price_min);
    const max = Number(product.price_max);
    const wasMin = Number(product.compare_at_price_min);
    const ranged = isFinite(max) && max > min;
    const current = ranged ? `${CFG.fromLabel} ${formatMoney(min)}` : formatMoney(min);
    if (wasMin > min) {
      return `<span class="ck-bcr__price price"><span class="price__current">${current}</span><del>${formatMoney(wasMin)}</del></span>`;
    }
    return `<span class="ck-bcr__price price"><span class="price__current">${current}</span></span>`;
  }

  function cardHTML(product) {
    const url = `${ROOT}/products/${product.handle}`;
    const image = imageFor(product);
    const media = image
      ? `<div class="media relative"><img class="img-fit" src="${image.src}&width=176" alt="${escapeAttr(image.alt)}" width="88" height="88" loading="lazy"></div>`
      : '<div class="media relative"></div>';

    /* Single-variant products add straight to cart, anything else goes to the
       product page to choose options. Same split the theme's own mini card
       makes, so the drawer behaves consistently whichever recommendations a
       shopper is looking at. */
    const variant = Array.isArray(product.variants) ? product.variants[0] : null;
    const single = product.total_variants === 1 && variant && variant.available;
    const action = single
      ? `<button type="button" class="btn btn--secondary btn--sm ck-bcr__add" data-variant-id="${variant.id}">${escapeAttr(CFG.addToCartLabel)}</button>`
      : `<a class="btn btn--secondary btn--sm ck-bcr__view" href="${url}">${escapeAttr(product.available ? CFG.chooseLabel : CFG.viewLabel)}</a>`;

    return `
      <li>
        <div class="card card--row card--related relative flex ck-bcr__card" data-product-id="${product.id}">
          <div class="card__media">${media}</div>
          <div class="card__info">
            <p class="h6 regular-text mt-2 mb-1 ck-bcr__title">
              <a href="${url}" class="card-link text-current ck-bcr__link">${escapeHTML(product.title)}</a>
            </p>
            ${priceHTML(product)}
            ${action}
          </div>
        </div>
      </li>`;
  }

  function escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function escapeAttr(value) {
    return escapeHTML(value).replace(/"/g, '&quot;');
  }

  /* ----------------------------------------------------------------- mount */

  const state = { signature: null, rid: null, products: [], widget: null, heading: '', inFlight: null, cartToken: null };

  /* The cart token is read here rather than from the boost_cart_token
     localStorage key, so it is the token as the cart currently stands. */
  function readCart() {
    return fetch(`${ROOT}/cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) return { ids: [], token: null };
        state.cartToken = cart.token || null;
        return { ids: [...new Set(cart.items.map((item) => item.product_id))], token: cart.token };
      })
      .catch(() => ({ ids: [], token: null }));
  }

  function bind(container) {
    if (container.dataset.ckBcrBound === 'true') return;
    container.dataset.ckBcrBound = 'true';

    container.addEventListener('click', async (event) => {
      const card = event.target.closest('.ck-bcr__card');
      if (!card) return;
      const product = state.products.find((p) => String(p.id) === card.dataset.productId);

      if (event.target.closest('.ck-bcr__link, .ck-bcr__view')) {
        track('view_product', state.rid, product, null, null);
        return;
      }

      const addButton = event.target.closest('.ck-bcr__add');
      if (!addButton) return;
      event.preventDefault();

      const variantId = addButton.dataset.variantId;
      addButton.setAttribute('disabled', 'disabled');
      try {
        const response = await fetch(`${ROOT}/cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });
        if (!response.ok) throw new Error(`add ${response.status}`);
        /* Read after the add, not before: an add into an empty cart creates the
           cart, so a token taken beforehand is null or stale. It comes from
           /cart.js rather than the add response, which returns the line item and
           carries no cart token at all. */
        const token = (await readCart()).token;
        track('add_to_cart', state.rid, product, variantId, token);
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      } catch (error) {
        window.location.href = `${ROOT}/products/${product ? product.handle : ''}`;
      } finally {
        addButton.removeAttribute('disabled');
      }
    });

    const nav = container.querySelector('.ck-bcr__nav');
    if (nav) {
      nav.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        const track_ = container.querySelector('.ck-bcr__track');
        if (!track_) return;
        const step = track_.clientWidth + 20;
        track_.scrollBy({ left: button.name === 'prev' ? -step : step, behavior: 'smooth' });
      });
    }
  }

  function paint(container) {
    const track_ = container.querySelector('.ck-bcr__track');
    const heading = container.querySelector('.ck-bcr__heading');
    if (!track_) return;

    if (!state.products.length) {
      container.classList.remove('is-populated');
      track_.innerHTML = '';
      return;
    }
    if (heading && state.heading) heading.textContent = state.heading;
    track_.innerHTML = state.products.map(cardHTML).join('');
    container.classList.add('is-populated');
    container.classList.toggle('ck-bcr--grid', state.layout !== 'carousel');
    container.querySelector('.ck-bcr__nav').hidden = state.products.length < 2 || state.layout !== 'carousel';
    bind(container);
  }

  async function mount() {
    const container = document.querySelector('.ck-bcr');
    if (!container) return;

    if (!state.widget) {
      const found = findWidgetConfig(CFG.wid);
      if (!found) return;
      state.widget = found.widget;
      const translated = found.translations && found.translations.recommendation;
      state.heading =
        CFG.heading ||
        (translated && translated[CFG.wid]) ||
        found.widget.widgetName ||
        '';
      state.layout = (found.widget.params && found.widget.params.layoutDisplay) || 'carousel';
    }

    const productIds = (await readCart()).ids;
    if (!productIds.length) {
      state.products = [];
      paint(container);
      return;
    }

    const signature = productIds.join(',');

    /* Re-rendering the drawer replaces this container, so a repaint is needed
       even when the cart itself has not changed. Only the network call is
       skipped. */
    if (signature === state.signature && state.products.length) {
      paint(container);
      return;
    }

    /* Adding from the widget fires the open, refresh and mutation paths at once,
       and the signature above is only updated once a response lands, so without
       a lock all three race through and fire the same request. Measured three
       identical calls per add before this. Later callers await the first. */
    if (!state.inFlight || state.inFlight.signature !== signature) {
      state.inFlight = { signature, promise: load(signature, productIds) };
    }
    container.classList.add('is-populated');
    await state.inFlight.promise;
    paint(container);
  }

  async function load(signature, productIds) {
    try {
      const data = await fetchRecommendations(state.widget, productIds);
      state.signature = signature;
      state.rid = data.meta && data.meta.rid;
      const inCart = new Set(productIds.map(String));
      const design = state.widget.widgetDesignSettings || {};
      const cap = design.numberOfRecommendProduct || (state.widget.params || {}).limit || 12;
      state.products = (data.products || [])
        .filter((product) => product && product.available && !inCart.has(String(product.id)))
        .slice(0, cap);
    } catch (error) {
      state.products = [];
    }
  }

  /* The drawer replaces its own innerHTML on every cart change, which takes this
     container with it, so mounting once on load is not enough. */
  let queued = false;
  function scheduleMount() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      mount();
    });
  }

  document.addEventListener('dispatch:cart-drawer:open', scheduleMount);
  document.addEventListener('dispatch:cart-drawer:refresh', scheduleMount);
  document.addEventListener('on:cart:change', scheduleMount);

  const drawer = document.getElementById('cart-drawer');
  if (drawer) {
    new MutationObserver(() => {
      const container = document.querySelector('.ck-bcr');
      if (container && container.dataset.ckBcrBound !== 'true') scheduleMount();
    }).observe(drawer, { childList: true, subtree: true });
  }

  scheduleMount();
})();
